import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, X, Minus, Loader2, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { AppHeader } from "@/components/AppHeader";
import { api, errMsg, fmtDate, duration } from "@/lib/api";

const LETTERS = "ABCDEF";

const ReviewItem = ({ r, i }) => {
  const status = r.chosen_index == null ? "skipped" : r.chosen_index === r.correct_index ? "correct" : "wrong";
  const tone = { correct: "text-green-700 bg-green-50 border-green-200", wrong: "text-red-700 bg-red-50 border-red-200", skipped: "text-slate-600 bg-slate-50 border-slate-200" }[status];
  const Icon = { correct: Check, wrong: X, skipped: Minus }[status];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid={`review-item-${i}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="small-label">Q{i + 1}</div>
        <span className={`inline-flex items-center gap-1 text-xs font-medium border rounded-full px-2.5 py-0.5 capitalize ${tone}`}><Icon className="h-3 w-3" />{status}</span>
      </div>
      <p className="font-heading font-semibold text-slate-900 mt-2 whitespace-pre-wrap">{r.text}</p>
      <div className="mt-4 space-y-2">
        {r.options.map((o, j) => {
          const correct = j === r.correct_index, chosen = j === r.chosen_index;
          const cls = correct ? "border-green-300 bg-green-50" : chosen ? "border-red-300 bg-red-50" : "border-slate-200";
          return (
            <div key={j} className={`flex items-center gap-3 border rounded-lg px-3 py-2 text-sm ${cls}`}>
              <span className="font-mono font-semibold text-slate-500 w-4">{LETTERS[j]}</span>
              <span className="flex-1">{o}</span>
              {correct && <span className="text-xs text-green-700 font-medium">Correct</span>}
              {chosen && !correct && <span className="text-xs text-red-700 font-medium">Your answer</span>}
            </div>
          );
        })}
      </div>
      {r.explanation && (
        <div className="mt-4 flex gap-2 text-sm bg-[#EFF6FF] text-slate-700 rounded-lg p-3">
          <Lightbulb className="h-4 w-4 text-[#2563EB] shrink-0 mt-0.5" />{r.explanation}
        </div>
      )}
    </div>
  );
};

export default function ResultPage({ admin = false }) {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const [res, setRes] = useState(null);

  useEffect(() => {
    api.get(admin ? `/admin/results/${attemptId}` : `/student/attempts/${attemptId}/result`)
      .then((r) => setRes(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [attemptId, admin]);

  const back = admin ? "/admin/results" : "/dashboard";
  const body = !res ? (
    <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
  ) : (
    <>
      <div className="fade-up bg-[#0F172A] grid-bg text-white rounded-2xl p-8 sm:p-10 mt-6 grid md:grid-cols-[1fr_auto] gap-8 items-end">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-blue-300">{admin ? `${res.student_name} · ${res.student_email}` : "Your Result"}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-3" data-testid="result-test-title">{res.test_title}</h1>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-4 text-sm text-slate-400">
            <span>Submitted {fmtDate(res.submitted_at)}</span><span>Time {duration(res.started_at, res.submitted_at)}</span><span>Pass mark {res.pass_percentage}%</span>
          </div>
          <Progress value={res.percentage} className="mt-6 h-2 bg-white/10 max-w-md" />
        </div>
        <div className="text-left md:text-right">
          <div className="font-heading text-6xl sm:text-7xl font-extrabold tabular-nums" data-testid="result-score-percentage">{Math.round(res.percentage)}%</div>
          <div className="font-mono text-slate-300 mt-1" data-testid="result-score">{res.score} / {res.total} correct</div>
          <Badge data-testid="result-passed-badge" className={`mt-3 text-sm ${res.passed ? "bg-green-500 hover:bg-green-500" : "bg-red-500 hover:bg-red-500"} text-white`}>
            {res.passed ? "PASSED" : "FAILED"}
          </Badge>
        </div>
      </div>
      <h2 className="font-heading text-lg font-semibold mt-12">Answer Review</h2>
      <div className="space-y-4 mt-4">{res.review.map((r, i) => <ReviewItem key={r.id} r={r} i={i} />)}</div>
    </>
  );

  return (
    <div className={admin ? "" : "min-h-screen bg-[#F8FAFC]"}>
      {!admin && <AppHeader />}
      <main className={admin ? "max-w-4xl" : "px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-10"}>
        <Button variant="ghost" onClick={() => nav(back)} data-testid="back-to-dashboard-button" className="hover:bg-slate-900 hover:text-white -ml-2">
          <ArrowLeft className="h-4 w-4 mr-1.5" />{admin ? "Back to results" : "Back to dashboard"}
        </Button>
        {body}
      </main>
    </div>
  );
}
